// Shape of `Job.output` for `HARMONIZE_VIOLATIONS` jobs.
//
// Single source of truth for the ECS task that produces the stats and the Oak
// frontend that renders them. Both import from here so the shape can't drift.

export type HarmonizeAirtableStats =
  | {
      error: string;
      /** Present when batches committed before the failure; used for partial-success reporting. */
      uploaded?: number;
    }
  | {
      uploaded: number;
      skippedExisting: number;
      facilitiesCreated: string[];
      duplicateIdsInBatch: string[];
    };

/**
 * Stats object stored in `Job.output` (and `HarmonizedViolationRefresh.stats`)
 * for a HARMONIZE_VIOLATIONS run when at least one tenant is flag-enabled.
 * For the no-tenants early-return path, see `HarmonizeNoTenantsStats`.
 */
export type HarmonizeRunStats = {
  sourceCounts: Record<string, number>;
  facilityMatchCount: number;
  programCounts: Record<string, number>;
  netNewCount: number;
  statusFlipCount: number;
  staleCount: number;
  /**
   * Rows across all three programs whose determinant indicates an active
   * formal EPA Order (`stdSeverityDeterminantCode === 'ORDER'`).
   *
   * A run-total of 0 is NOT on its own a signal that the rule broke. An Order
   * escalates only its own program's violations (RCRA→waste, CWA→water,
   * CAA→air), so a small enabled fleet legitimately sits at 0 — Amazon does,
   * because both of its in-window orders are under FIFRA. Zero only becomes
   * suspicious when there was qualifying input to match: the three ICIS-FEC
   * tables (`CaseEnforcements`, `CaseFacilities`, `CaseLawSections`) all
   * non-empty in `sourceCounts`, and at least one enabled-tenant facility
   * appearing in a case at all. `run.ts` makes exactly that distinction before
   * deciding whether to warn.
   *
   * Optional: rows persisted before this field existed lack it. Kept
   * optional (rather than required) so `isHarmonizeRunStats` still accepts
   * those historical rows and the staff Jobs dialog keeps rendering them
   * instead of going blank.
   */
  orderMatchCount?: number;
  /**
   * Rows whose determinant is a state-issued formal enforcement action
   * (`stdSeverityDeterminantCode === 'STATE_ORDER'`, waste-only today).
   * Optional for the same historical-rows reason as `orderMatchCount`.
   */
  stateOrderMatchCount?: number;
  /**
   * Human-readable ADVISORY guardrail breaches from Stage 0 (see
   * `stages/guardrails.ts`'s `TABLE_SEVERITY`) — e.g. an enrichment source
   * table (CaseEnforcements, CaseFacilities, NpdesPermit) came up empty.
   * These never fail the run; they are carried here purely so a past run
   * can be investigated. Optional: rows persisted before this field existed
   * lack it, and a run with zero advisory breaches may omit it too.
   */
  warnings?: string[];
  /**
   * Count of rows whose stored determinant is `PERMIT_EXPIRED` and whose
   * severity was protected from a downgrade this run because Stage 0 raised
   * `severityFreeze.permit` (NpdesPermit unhealthy). Populated only when that
   * flag was set; absent on healthy runs and on rows persisted before this
   * field existed.
   */
  frozenPermitCount?: number;
  /**
   * Count of rows whose stored determinant is `ORDER` and whose severity was
   * protected from a downgrade this run because Stage 0 raised
   * `severityFreeze.order` (the ICIS-FEC case tables unhealthy). Populated
   * only when that flag was set; absent on healthy runs and on rows
   * persisted before this field existed.
   */
  frozenOrderCount?: number;
  airtable: HarmonizeAirtableStats;
};

/** Minimal object returned when there are no enabled tenants. */
export type HarmonizeNoTenantsStats = {
  note: string;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isStringNumberRecord(v: unknown): v is Record<string, number> {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every((val) => typeof val === "number");
}

function isHarmonizeAirtableStats(v: unknown): v is HarmonizeAirtableStats {
  if (!isPlainObject(v)) return false;
  // Error shape
  if ("error" in v) {
    return typeof v.error === "string";
  }
  // Success shape
  if (typeof v.uploaded !== "number") return false;
  if (typeof v.skippedExisting !== "number") return false;
  if (!isStringArray(v.facilitiesCreated)) return false;
  if (!isStringArray(v.duplicateIdsInBatch)) return false;
  return true;
}

/**
 * Narrow a loosely-typed value into a `HarmonizeNoTenantsStats`.
 * Returns `true` when the object has exactly the `note` string field, which is
 * what `runHarmonizeViolations` returns on the no-enabled-tenants early path.
 */
export function isHarmonizeNoTenantsStats(value: unknown): value is HarmonizeNoTenantsStats {
  if (!isPlainObject(value)) return false;
  return typeof value.note === "string";
}

/**
 * Narrow a loosely-typed value (e.g. `Job.output` from the GraphQL `JSON`
 * scalar) into a `HarmonizeRunStats` without an `as` cast. Returns `true`
 * only when every required field is present with the correct type. An
 * upstream change that drops a field will fail this check up-front rather than
 * silently render empty cells in the staff-facing dialog.
 */
export function isHarmonizeRunStats(value: unknown): value is HarmonizeRunStats {
  if (!isPlainObject(value)) return false;
  if (!isStringNumberRecord(value.sourceCounts)) return false;
  if (typeof value.facilityMatchCount !== "number") return false;
  if (!isStringNumberRecord(value.programCounts)) return false;
  if (typeof value.netNewCount !== "number") return false;
  if (typeof value.statusFlipCount !== "number") return false;
  if (typeof value.staleCount !== "number") return false;
  // Optional: absent on rows persisted before this field existed.
  if ("orderMatchCount" in value && typeof value.orderMatchCount !== "number") return false;
  if ("stateOrderMatchCount" in value && typeof value.stateOrderMatchCount !== "number")
    return false;
  // Optional: absent on historical rows, and on runs with no advisory breaches.
  if ("warnings" in value && !isStringArray(value.warnings)) return false;
  // Optional: absent on historical rows, and on runs with no severity freeze.
  if ("frozenPermitCount" in value && typeof value.frozenPermitCount !== "number") return false;
  if ("frozenOrderCount" in value && typeof value.frozenOrderCount !== "number") return false;
  if (!isHarmonizeAirtableStats(value.airtable)) return false;
  return true;
}
